import 'symbol-observable';
import type { Observer, ObservableLike, Unsubscribable } from 'type-fest';

import {
  callCompleteObservers,
  callErrorObservers,
  callNextObservers,
  subscribeObserver,
} from '@frontend/utils';

/**
 * SignalProducer provides means to produce signals that can be observed by its Signal
 * @remarks SignalProducer class is not exported. Use the signalFactory(). Only its type is exported (below)
 */
class SignalProducer<T> {
  public isActive = true;
  private _subscriptions: Set<Partial<Observer<T>>> = new Set();

  public signal: Signal<T>;

  constructor() {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    this.signal = new Signal(this, this._subscriptions);
  }

  public next(v: T) {
    callNextObservers(this._subscriptions, v);
  }

  public complete() {
    callCompleteObservers(this._subscriptions);
  }

  public error(err: unknown) {
    callErrorObservers(this._subscriptions, err);
  }
}

/**
 * Signal provides means to observe (via subscribe()) to next, complete, error events produced by SignalProducer
 * @remarks Signal class is not exported. Use the signalFactory(). Only its type is exported (below)
 */
class Signal<T> implements ObservableLike<T> {
  constructor(
    private controller: SignalProducer<T>,
    private _subscriptions: Set<Partial<Observer<T>>>,
  ) {}

  public get isActive(): boolean {
    return this.controller.isActive;
  }

  public get subscriptionCount() {
    return this._subscriptions.size;
  }

  public subscribe(observer: Partial<Observer<T>>): Unsubscribable {
    return subscribeObserver(
      observer,
      this._subscriptions,
      this.controller.isActive,
    );
  }

  public unsubscribe(observer: Partial<Observer<T>>): boolean {
    return this._subscriptions.delete(observer);
  }

  public unsubscribeAll() {
    this._subscriptions.clear();
  }

  public [Symbol.observable](): this {
    return this;
  }
}

export const signalFactory = <T>(): {
  controller: SignalProducer<T>;
  signal: Signal<T>;
} => {
  const controller = new SignalProducer<T>();
  return { controller, signal: controller.signal };
};

//neverSignal: A signal that never signals
export const neverSignal: Signal<unknown> = (() => {
  const { controller, signal } = signalFactory();
  controller.isActive = false; //so siginal.subscribe() is noop (it never signals so no need for tracking subscriptions)
  return signal;
})();

export type { Signal, SignalProducer };
